import ObexRequestsHandlerP80 from '../handlers/ObexRequestsHandlerP80';
import { getAccessToken, getActiveCompanyId, buildQuery, unwrapList, unwrapRecord, unwrapRecordOrObject, unwrapError, getTransportError } from './apiUtils';

/** Cuenta contable propia de un cliente en la sociedad activa (subcuenta 430NNNN). */
export interface ClientAccount {
  clientId: string;
  accountId: string;
  code: string;
  name: string;
}

const mapClientAccount = (raw: any): ClientAccount => ({
  clientId:  String(raw.client_id),
  accountId: String(raw.account_id),
  code:      String(raw.code ?? ''),
  name:      raw.name || ''
});

/** Cuentas propias de los clientes de la sociedad activa, indexadas por id de cliente. */
export const listClientAccounts = async (): Promise<Record<string, ClientAccount>> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const res = await ObexRequestsHandlerP80.get(`/client_accounts_list${buildQuery({ access_token: token, company_id: getActiveCompanyId() })}`);
  const transportError = getTransportError(res);
  if (transportError) throw new Error(transportError);

  const out: Record<string, ClientAccount> = {};
  unwrapList(res).map(mapClientAccount).forEach(a => { out[a.clientId] = a; });
  return out;
};

/**
 * Devuelve la cuenta propia del cliente; si todavía no la tiene, la crea (siguiente 430NNNN libre).
 * Va por client_accounts_set con account_id=new: así solo hace falta esa API.
 */
export const ensureClientAccount = async (clientId: string): Promise<ClientAccount> => {
  const account = await setClientAccount(clientId, 'new');
  if (!account) throw new Error('No se pudo crear la cuenta del cliente.');
  return account;
};

/**
 * Asocia al cliente una cuenta 430NNNN que ya existe en el plan (client_accounts_set).
 * Con accountId vacío le quita la asociación (la cuenta sigue en el plan, libre para otro cliente).
 * Con accountId 'new' crea la siguiente cuenta 430NNNN libre y se la asocia (o devuelve la que ya tiene).
 */
export const setClientAccount = async (clientId: string, accountId: string): Promise<ClientAccount | null> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const res = await ObexRequestsHandlerP80.get(`/client_accounts_set${buildQuery({
    access_token: token,
    company_id:   getActiveCompanyId(),
    client_id:    clientId,
    account_id:   accountId
  })}`);
  const out = unwrapRecordOrObject(res);
  if (!out || (!out.message && !out.account_id)) throw new Error(unwrapError(res, 'No se pudo asociar la cuenta al cliente.'));
  return out.account_id ? mapClientAccount({ ...out, client_id: out.client_id ?? clientId }) : null;
};
